import { ChangeDetectionStrategy, Component, inject, input, InputSignal } from '@angular/core';
import { formatDate, DatePipe } from '@angular/common';
import { ProductTimelineEntry } from '../../models/product-timeline-entry';
import { OrderControllerService, OrderUtilityService } from './../../../../services';
import { OrderShipment } from './../../../../models/order-shipment';
import { OrderLineGroup } from '../../../../models';
import { LineStatus, OrderType } from '../../../../models/enums';
import { MatIcon } from '@angular/material/icon';
import { OrderLineLanguagesComponent } from '../../../../shared/components/order-line-languages/order-line-languages.component';

@Component({
  selector: 'insight-order-shipment-details-timeline',
  templateUrl: './order-shipment-details-timeline.component.html',
  styleUrls: ['./order-shipment-details-timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, DatePipe, OrderLineLanguagesComponent]
})
export class OrderShipmentDetailsTimelineComponent {
  public orderShipment: InputSignal<OrderShipment> = input.required<OrderShipment>();
  public orderTypeEnum = OrderType;
  public orderStatusEnum = LineStatus;

  // Injected services
  private orderUtilityService = inject(OrderUtilityService);
  private orderControllerService = inject(OrderControllerService);

  public calculateDisplayStatus(timelineEntry: ProductTimelineEntry): string {
    // step was not done
    if (timelineEntry.startTime === null && timelineEntry.endTime === null) {
      return 'Not done';
    }

    // step has started, but not finished
    if (timelineEntry.startTime !== null && timelineEntry.endTime === null) {
      return `Started ${formatDate(timelineEntry.startTime, 'M/d/yy @ h:mm a', 'en')}`;
    }

    // step was complete
    return `Completed ${formatDate(timelineEntry.endTime, 'M/d/yy @ h:mm a', 'en')}`;
  }

  public lineStatusDisplay(lineStatusIndex: number): string {
    return this.orderUtilityService.mapOrderShipmentStatus(lineStatusIndex);
  }

  public calculateStepStyle(timelineEntry: ProductTimelineEntry): string {
    const styleText = 'step-chip';

    // step was not done
    if (timelineEntry.startTime === null && timelineEntry.endTime === null) {
      return `${styleText} not-done`;
    }

    // step has started, but not finished
    if (timelineEntry.startTime !== null && timelineEntry.endTime === null) {
      return `${styleText} started`;
    }

    return `${styleText} completed`;
  }

  public linkToPubAdmin(productNumber: string) {
    this.orderControllerService.linkToPubAdmin(productNumber);
  }

  public calculateUploadStatusStyle(uploadDate: string, orderStatus: LineStatus): string {
    // if a line has been uploaded, it just a value...or not
    const styleText = 'step-chip';
    if (uploadDate != null || orderStatus === LineStatus.Complete || orderStatus === LineStatus.Shipped) {
      return `${styleText} completed`;
    }

    return styleText;
  }

  public displayTextForNullUploadedContent(orderStatus: LineStatus): string {
    if (orderStatus === LineStatus.Complete || orderStatus === LineStatus.Shipped) {
      return 'Not needed';
    }

    return 'Not done';
  }

  public calculateProcessingTime(fromDate: string | Date, toDate: string | Date): string {
    const from = new Date(fromDate).getTime();

    let to: number;
    if (toDate === null) {
      to = new Date().getTime(); // compare to now
    } else {
      to = new Date(toDate).getTime();
    }

    const processingHours = (to - from) / (1000 * 60 * 60);
    const processingMinutes = ((to - from) / (1000 * 60)) - (Math.floor(processingHours) * 60);

    const hoursString = Math.floor(processingHours) !== 0 ? `${Math.floor(processingHours)}h ` : '';
    const minutesString = Math.floor(processingMinutes) !== 0 ? `${Math.floor(processingMinutes)}m` : '';

    let formattedString = '';

    if (hoursString === '' && minutesString === '') {
      formattedString = '<1m';
    } else {
      formattedString = hoursString + minutesString;
    }

    return formattedString;
  }

  public okToShowStepInTimeline(statusEntry: ProductTimelineEntry): boolean {
    // The order line groups contain all the valid steps for an order type. All those calculations are done in C#
    // We can just query those to see what steps we need to show
    const didWeFindStatusInLineGroups = this.orderShipment().lineGroups.filter((group: OrderLineGroup) => group.status === statusEntry.id);
    return didWeFindStatusInLineGroups.length > 0;
  }
}
